import React, { useState, useEffect } from 'react';
import './index.css';

const Home = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);

    const handleFormSubmit = (event) => {
        event.preventDefault();
        setShowSuccessAlert(true);
        event.target.reset();
    };

    useEffect(() => {
        /* THREE.JS INTEGRATION - Light background interactive Emerald & Amber Gold Crystalline Structure */
        let scene, camera, renderer, crystalSystem, particlesSystem;
        let mouseX = 0, mouseY = 0;
        let targetX = 0, targetY = 0;
        let animationFrameId;

        let isMounted = true;

        function init3D() {
            const container = document.getElementById('hero-3d-canvas-container');
            if (!container) return;
            container.innerHTML = '';
            const width = container.clientWidth;
            const height = container.clientHeight;

            scene = new window.THREE.Scene();

            camera = new window.THREE.PerspectiveCamera(45, width / height, 0.1, 100);
            camera.position.z = 24;

            renderer = new window.THREE.WebGLRenderer({ antialias: true, alpha: true });
            renderer.setSize(width, height);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
            container.appendChild(renderer.domElement);

            crystalSystem = new window.THREE.Group();

            const coreGeometry = new window.THREE.IcosahedronGeometry(3, 1);
            const coreMaterial = new window.THREE.MeshPhongMaterial({
                color: 0x1c7441,
                wireframe: true,
                transparent: true,
                opacity: 0.8,
                shininess: 120
            });
            const mainCore = new window.THREE.Mesh(coreGeometry, coreMaterial);
            crystalSystem.add(mainCore);

            const outerGeometry = new window.THREE.DodecahedronGeometry(5, 1);
            const outerMaterial = new window.THREE.MeshPhongMaterial({
                color: 0xc59b27,
                wireframe: true,
                transparent: true,
                opacity: 0.6,
                shininess: 150
            });
            const outerOrbit = new window.THREE.Mesh(outerGeometry, outerMaterial);
            crystalSystem.add(outerOrbit);

            scene.add(crystalSystem);

            const particleCount = 140;
            const particlesGeometry = new window.THREE.BufferGeometry();
            const positions = new Float32Array(particleCount * 3);
            const colors = new Float32Array(particleCount * 3);

            const colorEmerald = new window.THREE.Color(0x1c7441);
            const colorGold = new window.THREE.Color(0xc59b27);

            for (let i = 0; i < particleCount; i++) {
                const radius = 6 + Math.random() * 12;
                const theta = Math.random() * Math.PI * 2;
                const phi = Math.acos((Math.random() * 2) - 1);

                positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
                positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
                positions[i * 3 + 2] = radius * Math.cos(phi);

                const mixedColor = new window.THREE.Color().lerpColors(colorEmerald, colorGold, Math.random());
                colors[i * 3] = mixedColor.r;
                colors[i * 3 + 1] = mixedColor.g;
                colors[i * 3 + 2] = mixedColor.b;
            }

            particlesGeometry.setAttribute('position', new window.THREE.BufferAttribute(positions, 3));
            particlesGeometry.setAttribute('color', new window.THREE.BufferAttribute(colors, 3));

            const pMaterial = new window.THREE.PointsMaterial({
                size: 0.22,
                vertexColors: true,
                transparent: true,
                opacity: 0.9,
                sizeAttenuation: true
            });

            particlesSystem = new window.THREE.Points(particlesGeometry, pMaterial);
            scene.add(particlesSystem);

            const ambientLight = new window.THREE.AmbientLight(0xffffff, 0.7);
            scene.add(ambientLight);

            const directionalLight = new window.THREE.DirectionalLight(0xc59b27, 1.4);
            directionalLight.position.set(5, 10, 7);
            scene.add(directionalLight);

            const emeraldLight = new window.THREE.PointLight(0x1c7441, 2.5, 15);
            emeraldLight.position.set(-4, -4, 4);
            scene.add(emeraldLight);

            document.addEventListener('mousemove', onDocumentMouseMove);
            window.addEventListener('resize', onWindowResize);
        }

        function onDocumentMouseMove(event) {
            mouseX = (event.clientX - window.innerWidth / 2) / 100;
            mouseY = (event.clientY - window.innerHeight / 2) / 100;
        }

        function onWindowResize() {
            const container = document.getElementById('hero-3d-canvas-container');
            if (!container || !camera || !renderer) return;
            const width = container.clientWidth;
            const height = container.clientHeight;

            camera.aspect = width / height;
            camera.updateProjectionMatrix();

            renderer.setSize(width, height);
        }

        function animate() {
            if (!isMounted) return;
            animationFrameId = requestAnimationFrame(animate);

            targetX += (mouseX - targetX) * 0.05;
            targetY += (mouseY - targetY) * 0.05;

            if (crystalSystem) {
                crystalSystem.rotation.y += 0.003;
                crystalSystem.rotation.x += 0.001;

                crystalSystem.rotation.y += targetX * 0.08;
                crystalSystem.rotation.x += targetY * 0.08;
            }

            if (particlesSystem) {
                particlesSystem.rotation.y -= 0.001;
                particlesSystem.rotation.x += 0.0005;
            }

            if (renderer && scene && camera) {
                renderer.render(scene, camera);
            }
        }

        const initInterval = setInterval(() => {
            if (window.THREE && window.gsap) {
                clearInterval(initInterval);
                init3D();
                animate();

                window.gsap.fromTo(".hero-text-anim", { y: -20, opacity: 0 }, { duration: 1, y: 0, opacity: 1, ease: "power3.out" });
                window.gsap.fromTo(".hero-title-anim", { y: 50, opacity: 0 }, { duration: 1.2, y: 0, opacity: 1, delay: 0.2, ease: "power4.out" });
                window.gsap.fromTo(".hero-desc-anim", { y: 30, opacity: 0 }, { duration: 1.2, y: 0, opacity: 1, delay: 0.4, ease: "power4.out" });
                window.gsap.fromTo(".hero-card-anim", { y: 40, opacity: 0, scale: 0.9 }, { duration: 1.0, y: 0, opacity: 1, scale: 1, delay: 0.6, stagger: 0.2, ease: "back.out(1.2)" });
                window.gsap.fromTo(".hero-btn-anim", { scale: 0.9, opacity: 0 }, { duration: 0.8, scale: 1, opacity: 1, delay: 0.9, stagger: 0.1, ease: "power4.out" });
            }
        }, 100);

        return () => {
            isMounted = false;
            clearInterval(initInterval);
            document.removeEventListener('mousemove', onDocumentMouseMove);
            window.removeEventListener('resize', onWindowResize);
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            if (renderer && renderer.domElement) {
                renderer.domElement.remove();
            }
        };
    }, []);

    return (
        <div className="font-sans antialiased selection:bg-brand-emerald selection:text-white">

            {/* HEADER & NAVIGATION */}
            <header className="fixed top-0 left-0 w-full z-50 glass border-b border-brand-emerald/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">

                    <div className="flex items-center">
                        <a href="#" className="flex items-center gap-3 group">
                            <div className="kps-desktop-logo items-center gap-3">
                                <img src="/logo.png" alt="KPS Logo" className="w-20 h-18 object-contain transition-transform duration-300 group-hover:scale-110" />
                                <div className="flex flex-col">
                                    {/* <span className="text-brand-pine font-display font-bold text-xl tracking-wider">KPS <span className="text-brand-gold">GROUP</span></span> */}
                                    {/* <span className="text-[10px] text-brand-emerald uppercase tracking-widest font-semibold">Enterprise Hub [Desktop]</span> */}
                                </div>
                            </div>

                            <div className="kps-tablet-logo items-center gap-2">
                                <img src="/logo.png" alt="KPS Logo" className="w-16 h-14 object-contain" />
                                <div className="flex flex-col">
                                    {/* <span className="text-brand-pine font-display font-semibold text-lg">KPS</span> */}
                                    {/* <span className="text-[8px] text-brand-gold uppercase tracking-widest font-bold">Tablet View</span> */}
                                </div>
                            </div>

                            <div className="kps-mobile-logo items-center gap-2">
                                <img src="/logo.png" alt="KPS Logo" className="w-16 h-14 object-contain" />
                                {/* <span className="text-brand-pine font-display font-medium text-base">KPS</span> */}
                            </div>
                        </a>
                    </div>

                    <nav className="kps-desktop-nav items-center gap-8">
                        <a href="#web-development" className="text-brand-pine/80 hover:text-brand-emerald transition-colors font-semibold text-sm tracking-wide">Web Solutions</a>
                        <a href="#products" className="text-brand-pine/80 hover:text-brand-emerald transition-colors font-semibold text-sm tracking-wide">Products</a>
                        <a href="#services" className="text-brand-pine/80 hover:text-brand-emerald transition-colors font-semibold text-sm tracking-wide">Services</a>
                        <a href="#b2b-b2c" className="text-brand-pine/80 hover:text-brand-emerald transition-colors font-semibold text-sm tracking-wide">B2B & B2C</a>
                        <a href="#customers" className="text-brand-pine/80 hover:text-brand-emerald transition-colors font-semibold text-sm tracking-wide">Customers</a>
                        <a href="#contact" className="text-brand-pine/80 hover:text-brand-emerald transition-colors font-semibold text-sm tracking-wide">Contact</a>
                    </nav>

                    <div className="kps-desktop-nav items-center gap-4">
                        <a href="mailto:contact@kpsgrouplts.com" className="text-sm font-bold text-brand-emerald hover:text-brand-gold transition-colors">
                            <i className="fa-regular fa-envelope mr-1"></i> Quick Mail
                        </a>
                        <a href="#contact" className="relative group overflow-hidden px-5 py-2.5 rounded-lg bg-gradient-to-r from-brand-emerald to-brand-pine text-white font-bold text-sm tracking-wide transition-all hover:scale-105 active:scale-95 duration-200 shadow-md">
                            Get in Touch
                        </a>
                    </div>

                    <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="kps-mobile-menu-btn text-brand-pine hover:text-brand-emerald focus:outline-none p-2" aria-label="Toggle Menu">
                        <i className="fa-solid fa-bars text-2xl"></i>
                    </button>
                </div>

                <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden fixed inset-x-0 top-20 bg-brand-champagne border-b border-brand-emerald/10 px-4 py-6 space-y-4 shadow-xl transition-all duration-300`}>
                    <nav className="flex flex-col gap-4">
                        <a href="#web-development" onClick={() => setIsMobileMenuOpen(false)} className="text-brand-pine hover:text-brand-emerald text-base font-semibold py-2 border-b border-brand-pine/10">Web Solutions</a>
                        <a href="#products" onClick={() => setIsMobileMenuOpen(false)} className="text-brand-pine hover:text-brand-emerald text-base font-semibold py-2 border-b border-brand-pine/10">Products</a>
                        <a href="#services" onClick={() => setIsMobileMenuOpen(false)} className="text-brand-pine hover:text-brand-emerald text-base font-semibold py-2 border-b border-brand-pine/10">Services</a>
                        <a href="#b2b-b2c" onClick={() => setIsMobileMenuOpen(false)} className="text-brand-pine hover:text-brand-emerald text-base font-semibold py-2 border-b border-brand-pine/10">B2B & B2C Hub</a>
                        <a href="#customers" onClick={() => setIsMobileMenuOpen(false)} className="text-brand-pine hover:text-brand-emerald text-base font-semibold py-2 border-b border-brand-pine/10">Our Customers</a>
                        <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="text-brand-pine hover:text-brand-emerald text-base font-semibold py-2 border-b border-brand-pine/10">Contact Us</a>
                    </nav>
                    <div className="flex flex-col gap-3 pt-2">
                        <a href="tel:+918328127946" className="w-full text-center py-2.5 rounded-lg border border-brand-emerald/30 text-brand-emerald font-bold text-sm bg-white">
                            <i className="fa-solid fa-phone mr-2"></i> Call Now
                        </a>
                        <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="w-full text-center py-2.5 rounded-lg bg-gradient-to-r from-brand-emerald to-brand-gold text-white font-bold text-sm">
                            Request Consultation
                        </a>
                    </div>
                </div>
            </header>

            <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden bg-gradient-to-b from-brand-alabaster via-brand-champagne to-brand-alabaster">

                {/* 3D Canvas Centered Background */}
                <div className="absolute inset-0 flex items-center justify-center z-0 pointer-events-none">
                    <div id="hero-3d-canvas-container" className="w-[100vw] h-[100vh] lg:w-[1200px] lg:h-[800px] opacity-30 md:opacity-40 pointer-events-auto cursor-grab active:cursor-grabbing"></div>
                </div>

                <div className="absolute top-1/4 left-10 w-96 h-96 rounded-full bg-brand-emeraldLight/30 blur-[120px] pointer-events-none z-0"></div>
                <div className="absolute bottom-1/4 right-10 w-96 h-96 rounded-full bg-brand-goldLight/50 blur-[120px] pointer-events-none z-0"></div>

                <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24 flex flex-col items-center text-center">

                    <div className="mb-10 bg-white/70 backdrop-blur-md px-4 py-2 rounded-lg border border-brand-emerald/20 flex items-center gap-2 shadow-sm animate-bounce">
                        <i className="fa-solid fa-arrows-spin text-brand-emerald animate-spin"></i>
                        <span className="text-xs text-brand-pine font-semibold">Click & drag the 3D crystal behind to interact</span>
                    </div>

                    <div className="hero-text-anim inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-emerald/10 border border-brand-emerald/20 mb-8">
                        <span className="w-2.5 h-2.5 rounded-full bg-brand-emerald animate-pulse"></span>
                        <span className="text-xs font-bold text-brand-emerald tracking-wide uppercase">Next Generation Business Ecosystem</span>
                    </div>

                    <h1 className="hero-title-anim font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-tight text-brand-pine mb-8 drop-shadow-sm">
                        Empowering Enterprise, <br className="hidden sm:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-emerald to-brand-gold">
                            Elevating Financial Horizons.
                        </span>
                    </h1>

                    <p className="hero-desc-anim text-brand-sage text-lg sm:text-2xl max-w-3xl font-medium leading-relaxed mb-10 drop-shadow-sm text-balance">
                        KPS orchestrates industry-leading digital products, customized retail strategies, comprehensive lending structures, and end-to-end B2B and B2C solutions designed for hyper-scale expansion.
                    </p>

                    <div className="grid grid-cols-2 gap-6 max-w-lg w-full mb-10">
                        <div className="hero-card-anim glass p-6 rounded-2xl shadow-lg border-brand-emerald/20 hover:scale-105 hover:-translate-y-2 hover:shadow-brand-emerald/20 transition-all duration-300 ease-out cursor-default backdrop-blur-md bg-white/40">
                            <h4 className="text-brand-emerald font-display font-extrabold text-4xl sm:text-5xl mb-1">2+</h4>
                            <p className="text-brand-pine text-xs sm:text-sm font-bold uppercase tracking-wider">Flagship SaaS</p>
                        </div>
                        <div className="hero-card-anim glass-gold p-6 rounded-2xl shadow-lg border-brand-gold/30 hover:scale-105 hover:-translate-y-2 hover:shadow-brand-gold/20 transition-all duration-300 ease-out cursor-default backdrop-blur-md bg-white/40">
                            <h4 className="text-brand-gold font-display font-extrabold text-4xl sm:text-5xl mb-1">3+</h4>
                            <p className="text-brand-pine text-xs sm:text-sm font-bold uppercase tracking-wider">Global B2B Pillars</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-5">
                        <a href="#products" className="hero-btn-anim px-10 py-4 rounded-xl bg-gradient-to-r from-brand-emerald to-brand-pine text-white font-extrabold text-lg text-center shadow-xl hover:shadow-brand-emerald/30 hover:-translate-y-1 hover:scale-105 transition-all duration-300">
                            Explore Our Products
                        </a>
                        <a href="#services" className="hero-btn-anim px-10 py-4 rounded-xl glass border-2 border-brand-gold/40 hover:border-brand-gold hover:bg-brand-gold/10 text-brand-gold font-bold text-lg text-center transition-all hover:-translate-y-1 hover:scale-105 duration-300 backdrop-blur-md">
                            Financial Services
                        </a>
                    </div>
                </div>
            </section>

            <section id="web-development" className="relative py-24 bg-brand-alabaster overflow-hidden">
                <div className="absolute -top-40 left-10 w-80 h-80 rounded-full bg-brand-goldLight/20 blur-[120px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row items-center gap-12">
                        <div className="lg:w-1/2 space-y-8">
                            <div>
                                <span className="text-xs font-bold tracking-widest text-brand-emerald uppercase px-3 py-1 rounded-full bg-brand-emerald/10 border border-brand-emerald/20">IT & Digital Platform</span>
                                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-pine mt-4">
                                    World-Class Web Development
                                </h2>
                                <p className="text-brand-sage mt-6 text-base sm:text-lg leading-relaxed">
                                    Our core motive is to build world-class websites that are highly scalable, reliable, and 100% secured with industry-standard architectures. We deliver competitive digital solutions that elevate your enterprise globally.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-brand-emerald/10 flex items-center justify-center text-brand-emerald flex-shrink-0">
                                        <i className="fa-solid fa-server text-xl"></i>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-brand-pine">Scalable & Reliable</h4>
                                        <p className="text-brand-sage text-sm mt-1">Robust architectures designed to handle hyper-growth and high-traffic seamlessly.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-brand-gold/10 flex items-center justify-center text-brand-gold flex-shrink-0">
                                        <i className="fa-solid fa-shield-halved text-xl"></i>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-brand-pine">100% Secured</h4>
                                        <p className="text-brand-sage text-sm mt-1">Industry-standard security measures, robust data encryption, and vulnerability protection built-in.</p>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-brand-emerald/10 flex items-center justify-center text-brand-emerald flex-shrink-0">
                                        <i className="fa-solid fa-rocket text-xl"></i>
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-bold text-brand-pine">Competitive Edge</h4>
                                        <p className="text-brand-sage text-sm mt-1">State-of-the-art UI/UX design and bleeding-edge tech stacks to outpace market competitors.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-4">
                                <a href="#contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-brand-emerald to-brand-pine text-white font-bold text-sm tracking-wide hover:shadow-lg transition-all hover:-translate-y-0.5">
                                    Start Your Project <i className="fa-solid fa-arrow-right"></i>
                                </a>
                            </div>
                        </div>

                        <div className="lg:w-1/2 w-full relative group" style={{ perspective: '1200px' }}>

                            <style>{`
                                @keyframes custom-float-3d {
                                    0%, 100% { transform: rotateY(15deg) rotateX(-5deg) translateZ(60px) translateY(0px); }
                                    50% { transform: rotateY(15deg) rotateX(-5deg) translateZ(60px) translateY(-15px); }
                                }
                                .animate-float-3d {
                                    animation: custom-float-3d 5s ease-in-out infinite;
                                }
                            `}</style>

                            {/* Main Web/Commercial/E-commerce Platform */}
                            <div className="glass p-2 rounded-2xl border-brand-emerald/20 shadow-2xl relative z-10 bg-white/40 transition-transform duration-700 ease-out group-hover:rotate-0" style={{ transform: 'rotateY(-12deg) rotateX(4deg) translateZ(0px)', transformStyle: 'preserve-3d' }}>
                                <div className="bg-brand-pine w-full h-full rounded-xl overflow-hidden shadow-inner flex flex-col relative">
                                    {/* Browser Header */}
                                    <div className="flex items-center justify-between px-4 py-3 bg-brand-pine/95 border-b border-white/10">
                                        <div className="flex gap-2">
                                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        </div>
                                        <div className="hidden sm:block bg-white/10 rounded-md px-12 py-1 text-[10px] text-white/50 font-mono tracking-widest text-center mx-4 flex-1 max-w-[250px]">
                                            kps-commercial-b2b.com
                                        </div>
                                        <div className="w-4 h-4"></div> {/* spacer */}
                                    </div>
                                    {/* Web Content */}
                                    <div className="flex-1 p-6 flex flex-col gap-6">
                                        {/* Hero area of the mockup */}
                                        <div className="flex justify-between items-center border-b border-white/5 pb-4">
                                            <div className="space-y-2">
                                                <div className="h-6 w-32 bg-gradient-to-r from-brand-emerald to-brand-gold rounded-md"></div>
                                                <div className="h-3 w-48 bg-white/20 rounded-md"></div>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="h-8 w-20 hidden sm:block bg-brand-emerald/30 rounded-md border border-brand-emerald/50"></div>
                                                <div className="h-8 w-8 bg-white/10 rounded-md flex justify-center items-center">
                                                    <i className="fa-solid fa-cart-shopping text-white/50 text-xs"></i>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Products Grid / Dashboard */}
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pb-6">
                                            {[1, 2, 3].map(i => (
                                                <div key={i} className="bg-white/5 rounded-lg p-3 border border-white/10 flex flex-col gap-2">
                                                    <div className="h-16 w-full bg-brand-emerald/10 rounded-md flex items-center justify-center">
                                                        <i className="fa-solid fa-chart-area text-brand-emerald/50 text-xl"></i>
                                                    </div>
                                                    <div className="h-2 w-full bg-white/20 rounded-full mt-1"></div>
                                                    <div className="h-2 w-2/3 bg-brand-gold/40 rounded-full"></div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/5 font-display font-extrabold text-4xl sm:text-5xl pointer-events-none tracking-widest rotate-[-15deg] whitespace-nowrap">
                                            COMMERCIAL
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile App Floating Panel */}
                            <div className="absolute -right-2 sm:-right-8 -bottom-8 w-40 sm:w-48 h-[280px] sm:h-80 glass p-1.5 rounded-[2rem] border border-white/60 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.5)] z-30 transition-transform duration-700 ease-out group-hover:translate-y-[-10px] group-hover:rotate-y-0" style={{ transform: 'rotateY(-25deg) rotateX(10deg) translateZ(80px)' }}>
                                <div className="bg-brand-pine w-full h-full rounded-[1.7rem] overflow-hidden border-[4px] border-brand-pine flex flex-col relative shadow-inner">
                                    {/* Notch */}
                                    <div className="absolute top-0 inset-x-0 h-5 flex justify-center z-10">
                                        <div className="w-12 sm:w-16 h-4 bg-brand-pine rounded-b-xl"></div>
                                    </div>
                                    {/* Mobile Content */}
                                    <div className="flex-1 bg-gradient-to-b from-brand-pine via-brand-emerald/20 to-brand-pine pt-8 p-3 sm:p-4 flex flex-col gap-4">
                                        <div className="flex justify-between items-center">
                                            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/10 flex justify-center items-center">
                                                <i className="fa-solid fa-user text-white/70 text-[10px] sm:text-xs"></i>
                                            </div>
                                            <div className="flex gap-2 text-white/60 text-[10px] sm:text-xs">
                                                <i className="fa-brands fa-apple drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] text-white"></i>
                                                <i className="fa-brands fa-android drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] text-brand-emerald"></i>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <div className="text-white font-display text-xs sm:text-sm font-bold leading-tight">iOS & Android App</div>
                                            <div className="h-1 sm:h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                                                <div className="w-3/4 h-full bg-brand-emerald shadow-[0_0_10px_rgba(16,185,129,0.8)]"></div>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mt-auto">
                                            <div className="h-16 sm:h-20 bg-white/5 rounded-xl border border-white/10 flex items-center justify-center flex-col gap-1.5 relative overflow-hidden group/card">
                                                <div className="absolute inset-0 bg-brand-emerald/20 translate-y-full group-hover/card:translate-y-0 transition-transform"></div>
                                                <i className="fa-solid fa-chart-pie text-brand-emerald text-xs sm:text-sm relative z-10"></i>
                                                <div className="h-1 w-6 sm:w-8 bg-white/20 rounded-full relative z-10"></div>
                                            </div>
                                            <div className="h-16 sm:h-20 bg-brand-emerald/20 rounded-xl border border-brand-emerald/30 flex items-center justify-center flex-col gap-1.5">
                                                <i className="fa-solid fa-mobile-screen text-white text-xs sm:text-sm"></i>
                                                <div className="h-1 w-6 sm:w-8 bg-white/40 rounded-full"></div>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Home Bar */}
                                    <div className="h-1 w-1/3 bg-white/30 absolute bottom-1.5 left-1/2 -translate-x-1/2 rounded-full"></div>
                                </div>
                            </div>

                            {/* 3D App / Tech Stack Floating Panel */}
                            <div className="absolute -left-2 sm:-left-10 top-1/4 w-32 sm:w-40 h-32 sm:h-40 glass p-2 rounded-2xl border border-brand-gold/40 shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] z-20 flex flex-col justify-center items-center gap-2 sm:gap-3 animate-float-3d cursor-pointer hover:scale-110 transition-transform duration-300">
                                <div className="relative w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center group/cube">
                                    <div className="absolute inset-0 bg-brand-gold/20 rounded-xl rotate-45 border border-brand-gold/50 transition-transform duration-500 group-hover/cube:rotate-[135deg]"></div>
                                    <i className="fa-solid fa-vr-cardboard text-2xl sm:text-3xl text-brand-gold relative z-10 drop-shadow-md"></i>
                                </div>
                                <div className="text-center">
                                    <div className="text-brand-pine font-extrabold text-xs sm:text-sm leading-tight">3D Render Apps</div>
                                    <div className="text-brand-sage text-[8px] sm:text-[9px] font-bold uppercase tracking-wider mt-1">WebGL Engine</div>
                                </div>
                            </div>

                            {/* Decorative Background Elements */}
                            <div className="absolute top-10 -right-10 w-full h-full bg-gradient-to-tr from-brand-emerald/30 to-brand-gold/20 rounded-3xl -z-10 rotate-6 blur-md transition-all duration-700 group-hover:rotate-12 group-hover:scale-105"></div>
                            <div className="absolute -bottom-10 -left-10 w-full h-full bg-brand-champagne/80 rounded-3xl border border-brand-emerald/20 -z-20 -rotate-3 backdrop-blur-sm transition-all duration-700 group-hover:-rotate-6 group-hover:scale-105"></div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="customers" className="relative py-16 bg-brand-champagne border-y border-brand-sand/30 overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <span className="text-[11px] font-bold tracking-widest text-brand-gold uppercase">Strategic Client Base</span>
                        <h2 className="font-display text-2xl font-bold text-brand-pine mt-1">Driving Growth For Global Leaders</h2>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 items-center justify-items-center">
                        <div className="w-full max-w-[180px] h-20 glass rounded-xl flex items-center justify-center p-4 hover:border-brand-emerald/40 transition-all duration-300 group">
                            <div className="text-center">
                                <span className="text-brand-sage group-hover:text-brand-pine font-display font-black text-xl tracking-wider transition-colors">NERUS</span>
                                <div className="w-full h-[2px] bg-brand-emerald scale-x-0 group-hover:scale-x-100 transition-transform duration-300 mt-1"></div>
                            </div>
                        </div>

                        <div className="w-full max-w-[180px] h-20 glass-gold rounded-xl flex items-center justify-center p-4 hover:border-brand-gold/40 transition-all duration-300 group">
                            <div className="text-center">
                                <span className="text-brand-sage group-hover:text-brand-pine font-display font-bold text-base tracking-widest transition-colors">KKB <span className="text-brand-gold font-light">WEALTH</span></span>
                                <div className="w-full h-[2px] bg-brand-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 mt-1"></div>
                            </div>
                        </div>

                        <div className="w-full max-w-[180px] h-20 glass rounded-xl flex items-center justify-center p-4 hover:border-brand-emerald/40 transition-all duration-300 group">
                            <div className="text-center">
                                <span className="text-brand-sage group-hover:text-brand-pine font-display font-medium text-lg tracking-wide transition-colors">Cornerstone</span>
                                <div className="w-full h-[2px] bg-brand-emerald scale-x-0 group-hover:scale-x-100 transition-transform duration-300 mt-1"></div>
                            </div>
                        </div>

                        <div className="w-full max-w-[180px] h-20 glass-gold rounded-xl flex items-center justify-center p-4 hover:border-brand-gold/40 transition-all duration-300 group">
                            <div className="text-center">
                                <span className="text-brand-sage group-hover:text-brand-pine font-display font-extrabold text-sm tracking-normal transition-colors">CENTARAUS <span className="text-brand-emerald font-light">PHARMA</span></span>
                                <div className="w-full h-[2px] bg-brand-emerald scale-x-0 group-hover:scale-x-100 transition-transform duration-300 mt-1"></div>
                            </div>
                        </div>

                        <div className="w-full max-w-[180px] h-20 glass rounded-xl flex items-center justify-center p-4 hover:border-brand-emerald/40 transition-all duration-300 group col-span-2 sm:col-span-1">
                            <div className="text-center">
                                <span className="text-brand-sage group-hover:text-brand-pine font-display font-semibold text-lg tracking-tight transition-colors">DD<span className="text-brand-gold font-bold">Abroad</span></span>
                                <div className="w-full h-[2px] bg-brand-gold scale-x-0 group-hover:scale-x-100 transition-transform duration-300 mt-1"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="products" className="relative py-24 bg-brand-alabaster overflow-hidden">
                <div className="absolute -top-40 right-10 w-80 h-80 rounded-full bg-brand-emeraldLight/20 blur-[120px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <span className="text-xs font-bold tracking-widest text-brand-emerald uppercase px-3 py-1 rounded-full bg-brand-emerald/10 border border-brand-emerald/20">SaaS Offerings</span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-pine mt-4">
                            Pioneering Digital Solutions
                        </h2>
                        <p className="text-brand-sage mt-4 text-base sm:text-lg">
                            Discover high-performance software environments structured to simplify high-frequency calculations and state schema processes.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                        <div className="relative glass rounded-3xl p-8 sm:p-12 overflow-hidden hover:border-brand-emerald/40 group transition-all duration-300 flex flex-col justify-between shadow-lg glow-emerald">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-emeraldLight/20 rounded-full blur-3xl group-hover:bg-brand-emeraldLight/40 transition-all"></div>

                            <div>
                                <div className="w-16 h-16 rounded-2xl bg-brand-emerald/10 border border-brand-emerald/20 flex items-center justify-center mb-8">
                                    <i className="fa-solid fa-receipt text-brand-emerald text-3xl"></i>
                                </div>

                                <h3 className="font-display text-2xl sm:text-3xl font-bold text-brand-pine mb-4">Billing Pro</h3>

                                <div className="inline-block px-3 py-1 rounded-full bg-brand-emerald/10 border border-brand-emerald/30 text-xs font-bold text-brand-emerald mb-6">
                                    Next-Gen Invoicing & Financial Audits
                                </div>

                                <p className="text-brand-sage text-sm sm:text-base mb-8 leading-relaxed">
                                    An ultra-responsive platform tailored for dynamic industries requiring robust real-time billing structures, multi-currency processing, automatic ledger adjustments, and instant receipt generation.
                                </p>

                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-center gap-3 text-brand-pine/90 text-sm font-medium">
                                        <i className="fa-solid fa-circle-check text-brand-emerald"></i> Real-time ledger updates & immediate tax processing.
                                    </li>
                                    <li className="flex items-center gap-3 text-brand-pine/90 text-sm font-medium">
                                        <i className="fa-solid fa-circle-check text-brand-emerald"></i> Integrated API endpoints for easy ERP synchronizations.
                                    </li>
                                    <li className="flex items-center gap-3 text-brand-pine/90 text-sm font-medium">
                                        <i className="fa-solid fa-circle-check text-brand-emerald"></i> Bank-grade security encryption standards built in.
                                    </li>
                                </ul>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-brand-champagne">
                                <a href="#contact" className="px-6 py-3 rounded-xl bg-brand-emerald text-white font-extrabold text-sm text-center hover:bg-brand-emerald/90 transition-all shadow-sm">
                                    Request Live Demo
                                </a>
                                <button onClick={() => window.location.hash = '#contact'} className="px-6 py-3 rounded-xl border border-brand-sage/20 hover:border-brand-emerald text-brand-sage hover:text-brand-emerald transition-all text-sm font-semibold">
                                    Download Feature Sheet
                                </button>
                            </div>
                        </div>

                        <div className="relative glass-gold rounded-3xl p-8 sm:p-12 overflow-hidden hover:border-brand-gold/40 group transition-all duration-300 flex flex-col justify-between shadow-lg glow-gold">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-goldLight/50 rounded-full blur-3xl group-hover:bg-brand-goldLight/80 transition-all"></div>

                            <div>
                                <div className="w-16 h-16 rounded-2xl bg-brand-gold/10 border border-brand-gold/20 flex items-center justify-center mb-8">
                                    <i className="fa-solid fa-wallet text-brand-gold text-3xl"></i>
                                </div>

                                <h3 className="font-display text-2xl sm:text-3xl font-bold text-brand-pine mb-4">MPKBY Master Management</h3>

                                <div className="inline-block px-3 py-1 rounded-full bg-brand-gold/10 border border-brand-gold/30 text-xs font-bold text-brand-gold mb-6">
                                    Government Agent Scheme Automation
                                </div>

                                <p className="text-brand-sage text-sm sm:text-base mb-8 leading-relaxed">
                                    A highly optimized utility built from the ground up for agents and administrators of the MPKBY saving schemes. Reduce manual tracking error margins down to absolute zero with batch automated processes.
                                </p>

                                <ul className="space-y-3 mb-8">
                                    <li className="flex items-center gap-3 text-brand-pine/90 text-sm font-medium">
                                        <i className="fa-solid fa-circle-check text-brand-gold"></i> Fast auto-fill generation of client list files.
                                    </li>
                                    <li className="flex items-center gap-3 text-brand-pine/90 text-sm font-medium">
                                        <i className="fa-solid fa-circle-check text-brand-gold"></i> Dynamic commission calculations & interest metrics.
                                    </li>
                                    <li className="flex items-center gap-3 text-brand-pine/90 text-sm font-medium">
                                        <i className="fa-solid fa-circle-check text-brand-gold"></i> Seamless offline mode compatibility with automatic cloud sync.
                                    </li>
                                </ul>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-brand-champagne">
                                <a href="#contact" className="px-6 py-3 rounded-xl bg-brand-gold text-white font-extrabold text-sm text-center hover:bg-brand-gold/90 transition-all shadow-sm">
                                    Setup System Trial
                                </a>
                                <button onClick={() => window.location.hash = '#contact'} className="px-6 py-3 rounded-xl border border-brand-sage/20 hover:border-brand-gold text-brand-sage hover:text-brand-gold transition-all text-sm font-semibold">
                                    Watch Video Walkthrough
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <section id="services" className="relative py-24 bg-brand-champagne">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-emeraldLight/20 rounded-full blur-[160px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between mb-16 gap-6">
                        <div className="max-w-xl text-left">
                            <span className="text-xs font-bold tracking-widest text-brand-gold uppercase px-3 py-1 rounded-full bg-brand-gold/15 border border-brand-gold/20">Specialized Domain Services</span>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-pine mt-4">
                                Comprehensive Wealth & Asset Management
                            </h2>
                        </div>
                        <p className="text-brand-sage text-base max-w-md text-left lg:text-right font-medium">
                            KPS delivers top-tier financial support structures to keep you growing. Manage portfolio health, safeguard assets, and acquire liquidity fast.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="glass p-8 rounded-3xl group hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between min-h-[380px] shadow-sm">
                            <div>
                                <div className="w-14 h-14 rounded-2xl bg-brand-emerald/10 border border-brand-emerald/20 flex items-center justify-center mb-8 group-hover:bg-brand-emerald group-hover:text-white transition-all duration-300 text-brand-emerald">
                                    <i className="fa-solid fa-shield-halved text-2xl"></i>
                                </div>
                                <h3 className="font-display text-2xl font-bold text-brand-pine mb-3">Insurance</h3>
                                <p className="text-brand-sage text-sm leading-relaxed">
                                    Bespoke corporate and individual policies mapped specifically to reduce liabilities and cover unforeseen asset damages with rapid claim-resolution paths.
                                </p>
                            </div>
                            <div className="pt-6 border-t border-brand-sage/10 flex items-center justify-between">
                                <span className="text-brand-emerald text-xs font-bold tracking-wider uppercase">Risk Protection Model</span>
                                <a href="#contact" className="text-brand-pine group-hover:text-brand-emerald transition-colors"><i className="fa-solid fa-arrow-right-long"></i></a>
                            </div>
                        </div>

                        <div className="glass p-8 rounded-3xl group hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between min-h-[380px] shadow-sm">
                            <div>
                                <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 border border-brand-gold/20 flex items-center justify-center mb-8 group-hover:bg-brand-gold group-hover:text-white transition-all duration-300 text-brand-gold">
                                    <i className="fa-solid fa-chart-line text-2xl"></i>
                                </div>
                                <h3 className="font-display text-2xl font-bold text-brand-pine mb-3">Fixed/Recurring Deposit</h3>
                                <p className="text-brand-sage text-sm leading-relaxed">
                                    Unlock industry-leading returns on corporate capital and capital reserves with customized tenures designed for reliable, stress-free yield optimization.
                                </p>
                            </div>
                            <div className="pt-6 border-t border-brand-sage/10 flex items-center justify-between">
                                <span className="text-brand-gold text-xs font-bold tracking-wider uppercase">Guaranteed High Yields</span>
                                <a href="#contact" className="text-brand-pine group-hover:text-brand-gold transition-colors"><i className="fa-solid fa-arrow-right-long"></i></a>
                            </div>
                        </div>

                        <div className="glass p-8 rounded-3xl group hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between min-h-[380px] shadow-sm">
                            <div>
                                <div className="w-14 h-14 rounded-2xl bg-brand-emerald/10 border border-brand-emerald/20 flex items-center justify-center mb-8 group-hover:bg-brand-emerald group-hover:text-white transition-all duration-300 text-brand-emerald">
                                    <i className="fa-solid fa-hand-holding-dollar text-2xl"></i>
                                </div>
                                <h3 className="font-display text-2xl font-bold text-brand-pine mb-3">Loans</h3>
                                <p className="text-brand-sage text-sm leading-relaxed">
                                    Obtain instantaneous operational lines of credit and long-term funding solutions. High-limit provisions engineered with competitive interest rate grids.
                                </p>
                            </div>
                            <div className="pt-6 border-t border-brand-sage/10 flex items-center justify-between">
                                <span className="text-brand-emerald text-xs font-bold tracking-wider uppercase">Flexible Liquidity</span>
                                <a href="#contact" className="text-brand-pine group-hover:text-brand-emerald transition-colors"><i className="fa-solid fa-arrow-right-long"></i></a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="b2b-b2c" className="relative py-24 bg-brand-champagne border-t border-brand-sand/30 overflow-hidden">
                <div className="absolute bottom-0 right-0 w-[450px] h-[450px] bg-brand-emeraldLight/20 rounded-full blur-[140px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <span className="text-xs font-bold tracking-widest text-brand-emerald uppercase px-3 py-1 rounded-full bg-brand-emerald/10 border border-brand-emerald/20">Distribution & Supply Core</span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-pine mt-4">
                            Global Hardware Logistics
                        </h2>
                        <p className="text-brand-sage mt-4 text-base font-semibold">
                            Managing extensive pipelines for bulk device allocation and customized direct-to-consumer mobile retailing operations worldwide.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">

                        <div className="glass rounded-3xl p-8 sm:p-12 flex flex-col justify-between border-brand-emerald/20 relative overflow-hidden group shadow-sm">
                            <div className="absolute top-4 right-4 text-8xl font-display font-extrabold text-brand-emerald/[0.04] select-none pointer-events-none group-hover:text-brand-emerald/[0.08] transition-colors">
                                B2B
                            </div>

                            <div>
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="w-2.5 h-2.5 rounded-full bg-brand-emerald"></span>
                                    <span className="text-xs font-bold text-brand-emerald uppercase tracking-wider">High Volume / Enterprise Logistics</span>
                                </div>
                                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-pine mb-4">Mobile Wholesale</h3>
                                <p className="text-brand-sage text-sm sm:text-base leading-relaxed mb-6 font-medium">
                                    Supply chain capabilities configured to distribute millions of smart units monthly to telecommunication hubs, verified international vendors, and commercial resellers globally.
                                </p>

                                <div className="space-y-4 pt-4 border-t border-brand-champagne">
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-brand-emerald/10 text-brand-emerald"><i className="fa-solid fa-truck-ramp-box"></i></div>
                                        <div>
                                            <h4 className="text-sm font-bold text-brand-pine">Consolidated Express Logistics</h4>
                                            <p className="text-brand-sage text-xs mt-0.5 font-medium">Custom clearance protocols handling bulk dispatches safely across international zones.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-brand-emerald/10 text-brand-emerald"><i className="fa-solid fa-tags"></i></div>
                                        <div>
                                            <h4 className="text-sm font-bold text-brand-pine">Aggressive Wholesale Price Matrices</h4>
                                            <p className="text-brand-sage text-xs mt-0.5 font-medium">Tiered margin configurations structured exclusively for high-scale buyers.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8">
                                <a href="#contact" className="inline-flex items-center gap-2 text-brand-emerald font-bold text-sm hover:text-brand-emerald/80 transition-colors">
                                    Enquire About Wholesale Lots <i className="fa-solid fa-arrow-right"></i>
                                </a>
                            </div>
                        </div>

                        <div className="glass rounded-3xl p-8 sm:p-12 flex flex-col justify-between border-brand-gold/20 relative overflow-hidden group shadow-sm">
                            <div className="absolute top-4 right-4 text-8xl font-display font-extrabold text-brand-gold/[0.04] select-none pointer-events-none group-hover:text-brand-gold/[0.08] transition-colors">
                                B2C
                            </div>

                            <div>
                                <div className="flex items-center gap-3 mb-6">
                                    <span className="w-2.5 h-2.5 rounded-full bg-brand-gold"></span>
                                    <span className="text-xs font-bold text-brand-gold uppercase tracking-wider">Consumer Market Excellence</span>
                                </div>
                                <h3 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-pine mb-4">Mobiles Retail</h3>
                                <p className="text-brand-sage text-sm sm:text-base leading-relaxed mb-6 font-medium">
                                    Providing an unmatched consumer retail platform for standard and elite handset devices. Access to official manufacturer guarantees and intuitive consumer upgrade packages.
                                </p>

                                <div className="space-y-4 pt-4 border-t border-brand-champagne">
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-brand-gold/10 text-brand-gold"><i className="fa-solid fa-store"></i></div>
                                        <div>
                                            <h4 className="text-sm font-bold text-brand-pine">Premium Direct Channels</h4>
                                            <p className="text-brand-sage text-xs mt-0.5 font-medium">Digital portal and physical location frameworks with full customer support accessibility.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-4">
                                        <div className="p-2 rounded-lg bg-brand-gold/10 text-brand-gold"><i className="fa-solid fa-arrows-rotate"></i></div>
                                        <div>
                                            <h4 className="text-sm font-bold text-brand-pine">Flexible Swap & Upgrade Options</h4>
                                            <p className="text-brand-sage text-xs mt-0.5 font-medium">Simplified handset trade-ins with immediate assessment algorithms.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8">
                                <a href="#contact" className="inline-flex items-center gap-2 text-brand-gold font-bold text-sm hover:text-brand-gold/80 transition-colors">
                                    Locate Retail Center / Buy Online <i className="fa-solid fa-arrow-right"></i>
                                </a>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <section id="contact" className="relative py-24 bg-brand-champagne border-t border-brand-sand/30 overflow-hidden">

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

                        <div className="lg:col-span-5 space-y-8">
                            <div>
                                <span className="text-xs font-bold tracking-widest text-brand-emerald uppercase px-3 py-1 rounded-full bg-brand-emerald/10 border border-brand-emerald/20">Reach Out</span>
                                <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-pine mt-4">
                                    Let's Connect & Innovate
                                </h2>
                                <p className="text-brand-sage mt-4 text-sm leading-relaxed font-semibold">
                                    Have questions regarding Billing Pro integrations or MPKBY enterprise updates? Our dynamic advisory staff is ready to organize a demonstration suited to your corporate needs.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <a href="mailto:contact@kpsgrouplts.com" className="block glass p-5 rounded-2xl border border-brand-emerald/10 hover:border-brand-emerald/50 group transition-all duration-300 shadow-sm">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-brand-emerald/10 flex items-center justify-center text-brand-emerald group-hover:bg-brand-emerald group-hover:text-white transition-all">
                                            <i className="fa-regular fa-envelope text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="text-brand-sage text-xs uppercase tracking-widest font-bold">Official Email Address</h4>
                                            <p className="text-brand-pine text-base font-extrabold select-all">contact@kpsgrouplts.com</p>
                                        </div>
                                    </div>
                                </a>

                                <a href="tel:+918328127946" className="block glass-gold p-5 rounded-2xl border border-brand-gold/10 hover:border-brand-gold/50 group transition-all duration-300 shadow-sm">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-brand-gold/10 flex items-center justify-center text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-all">
                                            <i className="fa-solid fa-phone text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="text-brand-sage text-xs uppercase tracking-widest font-bold">Instant Telephone Support</h4>
                                            <p className="text-brand-pine text-base font-extrabold select-all">+91 8328127946</p>
                                        </div>
                                    </div>
                                </a>
                            </div>

                            <div className="p-5 rounded-2xl border border-brand-sage/20 bg-brand-alabaster/85 flex items-center gap-3 shadow-inner">
                                <i className="fa-solid fa-clock text-brand-emerald text-lg animate-pulse"></i>
                                <span className="text-xs text-brand-pine font-bold">Standard Response Time: Under 2 Hours (Business Days)</span>
                            </div>
                        </div>

                        <div className="lg:col-span-7">
                            <div className="glass p-8 sm:p-10 rounded-3xl relative overflow-hidden shadow-md">

                                <form id="contact-form" className="space-y-6" onSubmit={handleFormSubmit}>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="name" className="text-xs font-bold text-brand-pine uppercase tracking-wider">Full Name *</label>
                                            <input type="text" id="name" required className="w-full bg-brand-alabaster border border-brand-sage/20 rounded-xl px-4 py-3.5 text-brand-pine placeholder-brand-sage/60 focus:outline-none focus:border-brand-emerald transition-colors" placeholder="Full name" />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="email" className="text-xs font-bold text-brand-pine uppercase tracking-wider">Email Address *</label>
                                            <input type="email" id="email" required className="w-full bg-brand-alabaster border border-brand-sage/20 rounded-xl px-4 py-3.5 text-brand-pine placeholder-brand-sage/60 focus:outline-none focus:border-brand-emerald transition-colors" placeholder="example@email.com" />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label htmlFor="phone" className="text-xs font-bold text-brand-pine uppercase tracking-wider">Contact Number</label>
                                            <input type="tel" id="phone" className="w-full bg-brand-alabaster border border-brand-sage/20 rounded-xl px-4 py-3.5 text-brand-pine placeholder-brand-sage/60 focus:outline-none focus:border-brand-emerald transition-colors" placeholder="+123 4567 890" />
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="interest" className="text-xs font-bold text-brand-pine uppercase tracking-wider">Subject Interest</label>
                                            <select id="interest" className="w-full bg-brand-alabaster border border-brand-sage/20 rounded-xl px-4 py-3.5 text-brand-pine focus:outline-none focus:border-brand-emerald transition-colors">
                                                <option value="Web Development">Web Development Services</option>
                                                <option value="Billing Pro">Billing Pro Software</option>
                                                <option value="MPKBY System">MPKBY Master Management</option>
                                                <option value="Financial Services">Financial Services (FD, Loans, Ins)</option>
                                                <option value="Wholesale Mobile">Mobile Wholesale (B2B)</option>
                                                <option value="Retail Mobile">Mobile Retail (B2C)</option>
                                                <option value="General Query">General Partner Inquiry</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label htmlFor="message" className="text-xs font-bold text-brand-pine uppercase tracking-wider">Inquiry details *</label>
                                        <textarea id="message" required rows="4" className="w-full bg-brand-alabaster border border-brand-sage/20 rounded-xl px-4 py-3.5 text-brand-pine placeholder-brand-sage/60 focus:outline-none focus:border-brand-emerald transition-colors" placeholder="How can KPS support your business expansion?"></textarea>
                                    </div>

                                    <button type="submit" className="w-full py-4 rounded-xl bg-gradient-to-r from-brand-emerald to-brand-pine hover:from-brand-emerald hover:to-brand-emerald hover:scale-[1.01] text-white font-extrabold text-base tracking-wide transition-all shadow-md">
                                        Submit
                                    </button>
                                </form>

                                {showSuccessAlert && (
                                    <div id="form-success-alert" className="absolute inset-0 bg-brand-alabaster/95 backdrop-blur-md flex flex-col items-center justify-center text-center p-8 space-y-4">
                                        <div className="w-20 h-20 rounded-full bg-brand-emerald/10 border border-brand-emerald/30 flex items-center justify-center text-brand-emerald text-4xl mb-2 animate-bounce">
                                            <i className="fa-solid fa-paper-plane"></i>
                                        </div>
                                        <h3 className="font-display text-2xl font-bold text-brand-pine">Transmission Successful</h3>
                                        <p className="text-brand-sage text-sm max-w-sm font-medium">
                                            Your secure communication has been registered. An executive from the KPS Advisory Hub will contact you shortly.
                                        </p>
                                        <button onClick={() => setShowSuccessAlert(false)} className="px-6 py-2 rounded-lg bg-brand-pine hover:bg-brand-emerald text-white text-xs font-bold tracking-wider uppercase transition-colors shadow-sm">
                                            Close Window
                                        </button>
                                    </div>
                                )}

                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <footer className="bg-brand-champagne text-brand-sage pt-20 pb-10 border-t border-brand-sand/40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 pb-16 border-b border-brand-sand/30">

                        <div className="lg:col-span-5 space-y-6">
                            <div className="flex items-center gap-3">
                                <img src="/logo.png" alt="KPS Logo" className="w-25 h-20 object-contain" />
                                {/* <span className="text-brand-pine font-display font-extrabold text-2xl tracking-wider">KPS <span className="text-brand-gold">GROUP</span></span> */}
                            </div>
                            <p className="text-brand-sage text-sm leading-relaxed max-w-sm font-semibold">
                                Driving efficiency in retail distributions, premium digital properties, wealth security networks, and global commercial integrations.
                            </p>
                            <div className="flex flex-wrap items-center gap-3">
                                <a href="#" className="w-10 h-10 rounded-lg bg-white border border-brand-sage/20 hover:border-brand-emerald flex items-center justify-center text-brand-sage hover:text-brand-emerald transition-all shadow-sm hover:scale-105" aria-label="Instagram">
                                    <i className="fa-brands fa-instagram text-lg"></i>
                                </a>
                                <a href="#" className="w-10 h-10 rounded-lg bg-white border border-brand-sage/20 hover:border-brand-emerald flex items-center justify-center text-brand-sage hover:text-brand-emerald transition-all shadow-sm hover:scale-105" aria-label="Facebook">
                                    <i className="fa-brands fa-facebook-f text-lg"></i>
                                </a>
                                <a href="#" className="w-10 h-10 rounded-lg bg-white border border-brand-sage/20 hover:border-brand-emerald flex items-center justify-center text-brand-sage hover:text-brand-emerald transition-all shadow-sm hover:scale-105" aria-label="YouTube">
                                    <i className="fa-brands fa-youtube text-lg"></i>
                                </a>
                                <a href="#" className="w-10 h-10 rounded-lg bg-white border border-brand-sage/20 hover:border-brand-emerald flex items-center justify-center text-brand-sage hover:text-brand-emerald transition-all shadow-sm hover:scale-105" aria-label="LinkedIn">
                                    <i className="fa-brands fa-linkedin-in text-lg"></i>
                                </a>
                                <a href="#" className="w-10 h-10 rounded-lg bg-white border border-brand-sage/20 hover:border-brand-emerald flex items-center justify-center text-brand-sage hover:text-brand-emerald transition-all shadow-sm hover:scale-105" aria-label="X (Twitter)">
                                    <i className="fa-brands fa-x-twitter text-lg"></i>
                                </a>
                            </div>
                        </div>

                        <div className="lg:col-span-4 grid grid-cols-2 gap-8">
                            <div>
                                <h4 className="text-sm font-bold tracking-widest text-brand-pine uppercase mb-4">Ecosystem</h4>
                                <ul className="space-y-3 font-semibold">
                                    <li><a href="#products" className="hover:text-brand-emerald transition-colors text-sm">Billing Pro</a></li>
                                    <li><a href="#products" className="hover:text-brand-emerald transition-colors text-sm">MPKBY System</a></li>
                                    <li><a href="#web-development" className="hover:text-brand-emerald transition-colors text-sm">Web Development</a></li>
                                    <li><a href="#services" className="hover:text-brand-emerald transition-colors text-sm">Insurance Plans</a></li>
                                    <li><a href="#services" className="hover:text-brand-emerald transition-colors text-sm">Fixed Deposits</a></li>
                                    <li><a href="#services" className="hover:text-brand-emerald transition-colors text-sm">Loans Structure</a></li>
                                </ul>
                            </div>
                            <div>
                                <h4 className="text-sm font-bold tracking-widest text-brand-pine uppercase mb-4">Logistics Core</h4>
                                <ul className="space-y-3 font-semibold">
                                    <li><a href="#b2b-b2c" className="hover:text-brand-gold transition-colors text-sm">B2B Wholesale</a></li>
                                    <li><a href="#b2b-b2c" className="hover:text-brand-gold transition-colors text-sm">B2C Retail Hub</a></li>
                                    <li><a href="#customers" className="hover:text-brand-gold transition-colors text-sm">Partners</a></li>
                                    <li><a href="#contact" className="hover:text-brand-gold transition-colors text-sm">Inquire Now</a></li>
                                </ul>
                            </div>
                        </div>

                        <div className="lg:col-span-3 space-y-4">
                            <h4 className="text-sm font-bold tracking-widest text-brand-pine uppercase mb-4">Contact Gateway</h4>
                            <p className="text-brand-sage text-xs font-semibold">Reach out for prompt answers and bespoke presentations.</p>
                            <div className="space-y-3 pt-2 font-bold">
                                <a href="mailto:contact@kpsgrouplts.com" className="flex items-center gap-3 text-sm text-brand-pine hover:text-brand-emerald transition-colors">
                                    <i className="fa-regular fa-envelope text-brand-emerald text-base"></i>
                                    <span className="truncate">contact@kpsgrouplts.com</span>
                                </a>
                                <a href="tel:+918328127946" className="flex items-center gap-3 text-sm text-brand-pine hover:text-brand-emerald transition-colors">
                                    <i className="fa-solid fa-phone text-brand-emerald text-base"></i>
                                    <span>+91 8328127946</span>
                                </a>
                            </div>
                        </div>

                    </div>

                    <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-brand-sage font-semibold">
                        <p>&copy; {new Date().getFullYear()} KPS Group Limited. All rights reserved | KPS ENTERPRISES</p>
                        <div className="flex gap-6">
                            <a href="#" className="hover:text-brand-pine transition-colors">Privacy Policy</a>
                            <a href="#" className="hover:text-brand-pine transition-colors">Terms of Operations</a>
                            <a href="#" className="hover:text-brand-pine transition-colors">Compliance Index</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Home;
